<template>
    <div>
        <select-input
            v-model="selectedTechnicalService"
            name="technical-service"
            :options="technicalServiceOptions"
        />
        <p style="white-space: pre;">
            {{ debug }}
        </p>
        <ul>
            <li v-for="technicalService in upsellTechnicalServices" :key="'upsell-' + technicalService.id">
                {{ technicalService.technical_name }}
            </li>
        </ul>
    </div>
</template>

<script>
    export default {
        props: {
            technicalServices: {
                type: Array,
                required: true,
            },
        },

        data() {
            return {
                debug: '',
                selectedTechnicalService: null,
                upsellTechnicalServices: [],
            };
        },

        computed: {
            technicalServiceOptions() {
                const options = {};

                this.technicalServices.forEach((technicalService) => {
                    options[technicalService.id] = technicalService.technical_name;
                });

                return options;
            },
        },

        watch: {
            selectedTechnicalService(id) {
                const technicalService = this.technicalServices.find(technicalService => technicalService.id === parseInt(id));

                const categorized = this.categorize(technicalService);

                this.upsellTechnicalServices = [];

                this.technicalServices.forEach(technicalService => {
                    const categorizedUpsell = this.categorize(technicalService);

                    if (categorizedUpsell.bindingTime !== categorized.bindingTime) return;
                    if (categorizedUpsell.capacityDown <= categorized.capacityDown) return;
                    if (categorizedUpsell.price <= categorized.price) return;
                    if (categorizedUpsell.service !== categorized.service) return;

                    this.upsellTechnicalServices.push(technicalService);
                });

                this.debug = (categorized.isValid ? '\n' : 'INVALID\n') +
                    'Binding Time: ' + (categorized.bindingTime > -1 ? categorized.bindingTime : 'None') + '\n' +
                    'Price: ' + (categorized.price > -1 ? categorized.price : 'None') + '\n' +
                    'Campaign: ' + categorized.campaign + '\n' +
                    'Capacity Down: ' + (categorized.capacityDown > -1 ? categorized.capacityDown : 'Unspecified') + '\n' +
                    'Capacity Up: ' + (categorized.capacityUp > -1 ? categorized.capacityUp : 'Unspecified') + '\n' +
                    'Service: ' + categorized.service + '\n' +
                    'Sub Service: ' + categorized.subService;
            },
        },

        methods: {
            categorize(technicalService) {
                const campaignMarkers = [
                    'C',
                    'OIC',
                    'XC',
                ];
                const data = {
                    bindingTime: -1,
                    campaign: '',
                    capacityDown: -1,
                    capacityUp: -1,
                    isValid: false,
                    price: -1,
                    service: '',
                    subService: '',
                };
                const name = technicalService.technical_name;

                if (! name) return data;
                if (name.indexOf(' ') !== -1) return data;
                if (name.indexOf('@') === -1) return data;

                let tempBindingTime = 0;

                const dashSplit = name.split('-');

                // Interpret service until capacity is found.
                while (dashSplit.length) {
                    const part = dashSplit.shift();

                    const capacity = this.getCapacity(part);

                    if (capacity.isValid) {
                        data.capacityDown = capacity.down;
                        data.capacityUp = capacity.up;

                        break;
                    }

                    data.service += part + ' ';
                }

                while (dashSplit.length) {
                    const part = dashSplit.shift();

                    const atSignSplit = part.split('@');

                    if (atSignSplit.length > 2) return data;

                    const beforeAtSign = atSignSplit[0];

                    if (beforeAtSign.indexOf('_') > -1) {
                        const underscoreSplit = beforeAtSign.split('_');

                        if (underscoreSplit.length !== 2) return data;

                        const campaign = underscoreSplit[0];

                        if (campaignMarkers.includes(campaign)) {
                            data.campaign += campaign + ' ';
                        } else {
                            return data;
                        }

                        const startPriceOrBindingTime = underscoreSplit[1];

                        if (! this.containsNumber(startPriceOrBindingTime)) return data;

                        if (startPriceOrBindingTime.indexOf('S') === 0) {
                            const startPrice = startPriceOrBindingTime.substring(1);

                            if (! this.isFloat(startPrice)) return data;

                            data.price = (data.price > -1 ? data.price : 0) + parseFloat(startPrice);
                        } else {
                            if (! this.isInteger(startPriceOrBindingTime)) return data;

                            tempBindingTime = parseInt(startPriceOrBindingTime);
                        }
                    } else {
                        if (atSignSplit.length < 2) {
                            data.subService += beforeAtSign + ' ';
                        } else {
                            if (! this.isInteger(beforeAtSign)) return data;

                            tempBindingTime = parseInt(beforeAtSign);
                        }
                    }

                    if (atSignSplit.length < 2) continue;

                    const afterAtSign = atSignSplit[1];

                    if (! this.isFloat(afterAtSign)) return data;

                    data.bindingTime = (data.bindingTime > -1 ? data.bindingTime : 0) + tempBindingTime;
                    data.price = (data.price > -1 ? data.price : 0) + tempBindingTime * parseFloat(afterAtSign);

                    tempBindingTime = 0;
                }

                // Tidy up.
                data.campaign = data.campaign.trim();
                data.price = Math.round(data.price * 100) / 100;
                data.service = data.service.trim();
                data.subService = data.subService.trim();

                data.isValid = true;

                return data;
            },
            containsNumber(text) {
                for (const char of text) {
                    if (char >= '0' && char <= '9') return true;
                }

                return false;
            },
            getCapacity(text) {
                const capacity = {
                    down: -1,
                    isValid: false,
                    up: -1,
                };

                if (! text) return capacity;

                const slashSplit = text.split('/');

                if (slashSplit.length === 1) {
                    if (! this.isInteger(slashSplit[0])) return capacity;

                    capacity.down = parseInt(slashSplit[0]);
                    capacity.isValid = true;

                    return capacity;
                } else if (slashSplit.length === 2) {
                    if (! this.isInteger(slashSplit[0])) return capacity;
                    if (! this.isInteger(slashSplit[1])) return capacity;

                    capacity.down = parseInt(slashSplit[0]);
                    capacity.up = parseInt(slashSplit[1]);
                    capacity.isValid = true;

                    return capacity;
                }

                return capacity;
            },
            isFloat(text) {
                for (const char of text) {
                    if (char !== '.' && (char < '0' || char > '9')) return false;
                }

                return true;
            },
            isInteger(text) {
                for (const char of text) {
                    if (char < '0' || char > '9') return false;
                }

                return true;
            },
        },
    };
</script>
